/**
 * Wizard shell — step navigation + active setup pane.
 */
import type { WizardState, WizardStep } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import { Pill } from '../ui/primitives';

const STEP_LABELS: Record<WizardStep, { title: string; description: string }> = {
  self: {
    title: '1. Self',
    description: 'Your own party used for margin tracking.',
  },
  uplines: {
    title: '2. Uplines',
    description: 'The supplier parties you import from.',
  },
  clients: {
    title: '3. Clients',
    description: 'POCs or people who hold accounts through you.',
  },
  accounts: {
    title: '4. Accounts',
    description: 'Accounts, aliases, and their first assignments.',
  },
  opening_balances: {
    title: '5. Opening balances',
    description: 'Carry over the balances you already trust.',
  },
  done: {
    title: 'Done',
    description: 'Finish setup and move into daily operations.',
  },
};

const ORDERED_STEPS: WizardStep[] = ['self', 'uplines', 'clients', 'accounts', 'opening_balances'];

interface WizardShellProps {
  wizard: WizardState;
  activeStep: WizardStep;
  onStepSelect: (step: WizardStep) => void;
  children: React.ReactNode;
  extra?: React.ReactNode;
}

export function WizardShell({
  wizard,
  activeStep,
  onStepSelect,
  children,
  extra,
}: WizardShellProps) {
  const currentMeta = STEP_LABELS[activeStep];
  const completedCount = ORDERED_STEPS.filter((step) => wizard.stepStatus[step] === 'complete').length;

  return (
    <AppShell
      title="Setup"
      toolbar={
        <PageHeader
          section="accounts"
          title="Setup assistant"
          subtitle="Finish the first five steps once, then run the business from Batches, Ledgers, Cash, and Match."
          actions={
            <Pill tone={wizard.completed ? 'green' : 'amber'}>
              {wizard.completed ? 'setup complete' : `${completedCount}/5 complete`}
            </Pill>
          }
        />
      }
    >
      <div className="space-y-4">
        <MetricStrip
          section="accounts"
          columns={4}
          items={[
            { label: 'Progress', value: `${completedCount}/5`, note: 'core steps finished', tone: wizard.completed ? 'pos' : 'default' },
            { label: 'Uplines', value: String(wizard.counts.uplineParties), note: 'import sources' },
            { label: 'Accounts', value: String(wizard.counts.accounts), note: 'ready for weekly work' },
            { label: 'Balances', value: String(wizard.counts.openingBalances), note: 'carry-over entries' },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <SectionCard section="accounts" title="Steps">
            <div className="space-y-2">
              {ORDERED_STEPS.map((step) => {
                const status = wizard.stepStatus[step];
                const isActive = step === activeStep;
                const stepNumber = ORDERED_STEPS.indexOf(step) + 1;
                return (
                  <button
                    key={step}
                    type="button"
                    onClick={() => onStepSelect(step)}
                    className={[
                      'flex w-full items-start gap-3 rounded-[22px] border px-3.5 py-3 text-left transition-all duration-150',
                      isActive
                        ? 'border-[var(--sec-accounts)] bg-[var(--sec-accounts-tint)] shadow-sm'
                        : 'border-border/70 bg-surface hover:-translate-y-px hover:bg-surface-raised',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                        status === 'complete'
                          ? 'bg-pos text-white'
                          : isActive
                          ? 'bg-[var(--sec-accounts)] text-white'
                          : 'bg-surface-raised text-ink-muted ring-1 ring-border/60',
                      ].join(' ')}
                    >
                      {status === 'complete' ? '✓' : stepNumber}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-small font-semibold tracking-[-0.01em] text-ink">
                        {STEP_LABELS[step].title}
                      </span>
                      <span className="mt-1 block text-[12px] leading-5 text-ink-muted">
                        {STEP_LABELS[step].description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {extra && <div className="mt-4 border-t border-border/70 pt-4">{extra}</div>}
          </SectionCard>

          <div className="min-w-0 space-y-4">
            <ActionQueueCard
              section="accounts"
              eyebrow="Current step"
              title={currentMeta.title}
              description={currentMeta.description}
              meta={
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span>Self party</span>
                    <span className="font-medium text-ink">{wizard.counts.selfParties}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Clients</span>
                    <span className="font-medium text-ink">{wizard.counts.clientParties}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Aliases</span>
                    <span className="font-medium text-ink">{wizard.counts.aliases}</span>
                  </div>
                </div>
              }
            />
            <div className="min-w-0">{children}</div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
